import { useEffect, useRef } from "react";

/**
 * Click-to-draw replacement for the removed google.maps.drawing.DrawingManager.
 *
 * The Drawing Library was deprecated (Aug 2025) and decommissioned in the Maps
 * JavaScript API as of version 3.65 (May 2026), so `new drawingLib.DrawingManager()`
 * now throws. This hook reproduces the behaviour we relied on using only the core
 * Maps library (Polyline / Polygon), which is unaffected by the deprecation.
 *
 * Usage: click the map to drop vertices; double-click or the "Terminer" button to
 * finish. `onComplete({ coordinates })` fires with the drawn path; the in-progress
 * overlays are then cleared so the user can draw again.
 *
 * @param {object}   params
 * @param {google.maps.Map} params.map          map instance (from useMap())
 * @param {"POLYGON"|"POLYLINE"} params.type     shape to draw
 * @param {object}   params.polygonOptions       styling for polygon mode
 * @param {object}   params.polylineOptions      styling for polyline mode
 * @param {(result: {coordinates: {lat:number,lng:number}[]}) => void} params.onComplete
 * @param {boolean}  params.active               when false the listeners are torn down
 */
export const useMapDrawing = ({
  map,
  type = "POLYGON",
  polygonOptions = {},
  polylineOptions = {},
  onComplete,
  active = true,
}) => {
  // Keep mutable values in refs so styling/callback changes don't restart an
  // in-progress drawing (the effect only depends on map/type/active).
  const onCompleteRef = useRef(onComplete);
  const optionsRef = useRef({ polygonOptions, polylineOptions });
  onCompleteRef.current = onComplete;
  optionsRef.current = { polygonOptions, polylineOptions };

  useEffect(() => {
    if (!map || !active || !window.google?.maps) return undefined;

    const g = window.google.maps;
    const isPolygon = type === "POLYGON";
    const minPoints = isPolygon ? 3 : 2;
    const { polygonOptions: poly, polylineOptions: line } = optionsRef.current;

    let vertices = [];
    let clickTimer = null;

    // Line that traces the clicked vertices in both modes.
    const previewLine = new g.Polyline({
      map,
      path: [],
      clickable: false,
      strokeColor: (isPolygon ? poly.strokeColor : line.strokeColor) || "#096dd9",
      strokeWeight: (isPolygon ? poly.strokeWeight : line.strokeWeight) || 2,
      zIndex: 2,
    });

    // Translucent fill preview, polygon mode only.
    const previewFill = isPolygon
      ? new g.Polygon({
          map,
          paths: [],
          clickable: false,
          fillColor: poly.fillColor || "#1890ff",
          fillOpacity: poly.fillOpacity ?? 0.3,
          strokeOpacity: 0,
          zIndex: 1,
        })
      : null;

    const strokeColor =
      (isPolygon ? poly.strokeColor : line.strokeColor) || "#096dd9";

    // Fixed-size dots dropped at each clicked vertex so the path is visible from
    // the very first click. The first dot of a polygon is a larger, clickable
    // target used to close the shape.
    const vertexMarkers = [];

    const refreshPreview = () => {
      previewLine.setPath(vertices);
      if (previewFill) previewFill.setPaths(vertices);
    };

    const clearVertices = () => {
      vertices = [];
      vertexMarkers.forEach((m) => m.setMap(null));
      vertexMarkers.length = 0;
      refreshPreview();
    };

    // Declared before addVertex so the first dot's click handler can call it.
    let finish = () => {};

    const addVertex = (latLng) => {
      const isFirst = vertices.length === 0;
      const closable = isFirst && isPolygon;
      vertices.push(latLng);

      const marker = new g.Marker({
        map,
        position: latLng,
        clickable: closable,
        cursor: closable ? "pointer" : "default",
        title: closable ? "Cliquez ici pour fermer le polygone" : undefined,
        zIndex: 3,
        icon: {
          path: g.SymbolPath.CIRCLE,
          scale: isFirst ? 7 : 5,
          fillColor: isFirst ? strokeColor : "#ffffff",
          fillOpacity: 1,
          strokeColor,
          strokeWeight: 2,
        },
      });
      if (closable) marker.addListener("click", () => finish());
      vertexMarkers.push(marker);

      refreshPreview();
    };

    finish = () => {
      if (vertices.length < minPoints) {
        alert(
          isPolygon
            ? "Le polygone doit comporter au moins 3 points."
            : "La ligne doit comporter au moins 2 points."
        );
        return;
      }
      const coordinates = vertices.map((v) => ({ lat: v.lat, lng: v.lng }));
      clearVertices();
      onCompleteRef.current?.({ coordinates });
    };

    // Distinguish a real click (add vertex) from the first click of a
    // double-click (finish) by deferring the vertex add briefly.
    const clickListener = map.addListener("click", (e) => {
      if (!e.latLng) return;
      const latLng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      if (clickTimer) clearTimeout(clickTimer);
      clickTimer = setTimeout(() => {
        addVertex(latLng);
        clickTimer = null;
      }, 220);
    });

    const dblclickListener = map.addListener("dblclick", () => {
      if (clickTimer) {
        clearTimeout(clickTimer);
        clickTimer = null;
      }
      finish();
    });

    // Don't let the finishing double-click also zoom the map.
    const prevDblClickZoom = map.get("disableDoubleClickZoom");
    map.setOptions({ disableDoubleClickZoom: true });

    // On-map control bar: finish / clear.
    const controlDiv = document.createElement("div");
    controlDiv.style.cssText = "display:flex;gap:8px;margin-top:10px;";
    const makeButton = (label, bg) => {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = label;
      b.style.cssText = `background:${bg};color:#fff;border:none;padding:8px 14px;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,0.3);`;
      return b;
    };
    const finishBtn = makeButton(
      isPolygon ? "Terminer le polygone" : "Terminer la ligne",
      "#096dd9"
    );
    const clearBtn = makeButton("Effacer", "#ff4d4f");
    finishBtn.onclick = finish;
    clearBtn.onclick = clearVertices;
    controlDiv.appendChild(finishBtn);
    controlDiv.appendChild(clearBtn);
    map.controls[g.ControlPosition.TOP_CENTER].push(controlDiv);

    return () => {
      if (clickTimer) clearTimeout(clickTimer);
      clickListener.remove();
      dblclickListener.remove();
      previewLine.setMap(null);
      if (previewFill) previewFill.setMap(null);
      vertexMarkers.forEach((m) => m.setMap(null));

      const controls = map.controls[g.ControlPosition.TOP_CENTER];
      for (let i = 0; i < controls.getLength(); i += 1) {
        if (controls.getAt(i) === controlDiv) {
          controls.removeAt(i);
          break;
        }
      }
      map.setOptions({ disableDoubleClickZoom: prevDblClickZoom ?? false });
    };
  }, [map, type, active]);
};
